import React, { useState, useEffect, useCallback } from 'react'
import { formatDate } from '../../utils/format-date'
import { useCashSession } from '../../hooks/useCashSession'
import { useRole } from '../../hooks/useRole'
import { toast } from 'react-toastify'
import { getMovements, getCashSummary, withdrawCash } from '../../services/CashService'
import { api } from '../../utils/api'
import type { CashSummary, Movement } from '../../types/@types/cash'
import { SessionPanel } from './SessionPanel'
import { MovementList } from './MovementList'
import { MetricCard } from './MetricCard'
import { CloseModal } from './CloseModal'
import { WithdrawModal } from './WithdrawModal'
import {
  DollarSign,
  BarChart2,
  Calculator,
  ArrowDownCircle,
  MinusCircle,
  CreditCard,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { formatCOP } from '../../utils/money'

export default function CashSessionManager() {
  const { isAdmin } = useRole()
  const {
    session,
    loading,
    error,
    openSession,
    opening,
    closeSession,
    closing,
  } = useCashSession()
  const [amount, setAmount] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const [summary, setSummary] = useState<CashSummary | null>(null)
  const [movements, setMovements] = useState<Movement[]>([])
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [showClose, setShowClose] = useState(false)
  const [showWithdraw, setShowWithdraw] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const fetchData = useCallback(async () => {
    if (!session?.id) return
    try {
      setLoadingSummary(true)
      const mov = await getMovements(session.id).catch(() => [] as Movement[])
      setMovements(mov)
      
      const backendSummary = await getCashSummary(session.id).catch(() => null)
      const initialAmount = Number(session.initialAmount) || 0
      const withdrawals = mov.reduce(
        (acc, m) =>
          m && m.type === 'withdraw' ? acc + (Number(m.amount) || 0) : acc,
        0
      )
      const expenses = mov.reduce(
        (acc, m) =>
          m && m.type === 'expense' ? acc + (Number(m.amount) || 0) : acc,
        0
      )

      if (isAdmin) {
        const totalSales = backendSummary?.totalSales ?? mov.reduce(
          (acc, m) =>
            m && m.type === 'sale' ? acc + (Number(m.amount) || 0) : acc,
          0
        )
        const cashSales = backendSummary?.cashSales ?? mov.reduce(
          (acc, m) => {
            if (!m || m.type !== 'sale') return acc;
            const method = (m.method || '').toLowerCase()
            return (method === 'cash' || method === 'efectivo' || !method)
              ? acc + (Number(m.amount) || 0)
              : acc
          },
          0
        )
        const expectedInDrawer = backendSummary?.expectedCash ?? (initialAmount + cashSales - withdrawals - expenses)

        setSummary({
          isBlind: false,
          initialAmount,
          totalSales,
          cashSales,
          withdrawals,
          expenses,
          expectedInDrawer,
          paymentBreakdown: backendSummary?.paymentBreakdown,
        })
      } else {
        // Cierre Ciego: Ocultar ventas y saldo esperado a personal operativo
        setSummary({
          isBlind: true,
          initialAmount,
          totalSales: null,
          cashSales: null,
          withdrawals,
          expenses,
          expectedInDrawer: null,
          paymentBreakdown: [],
        })
      }
    } catch (err) {
      toast.error('No se pudo actualizar los totales')
    } finally {
      setLoadingSummary(false)
      setRefreshing(false)
    }
  }, [session, isAdmin])

  useEffect(() => {
    if (!session?.id) {
      setSummary(null)
      setMovements([])
      return
    }
    fetchData()
    const interval = setInterval(fetchData, 30000)
    const handler = () => fetchData()
    window.addEventListener('cash:updated', handler)
    return () => {
      clearInterval(interval)
      window.removeEventListener('cash:updated', handler)
    }
  }, [session, fetchData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (amount <= 0) {
      setFormError('Monto inicial inválido')
      return
    }
    setFormError(null)
    await openSession(amount)
  }

  const handleCloseSession = async (finalAmount: number, obs: string, cashCount: Record<string, number>, paymentBreakdown: any[]) => {
    const success = await closeSession(finalAmount, obs, cashCount, paymentBreakdown)
    if (success) {
      toast.success('Caja cerrada correctamente')
      setShowClose(false)
    } else {
      toast.error('No se pudo cerrar la caja')
    }
  }

  const handleWithdraw = async (
    amount: number,
    reason: string,
    kind?: string,
    reference?: string,
    openDrawer?: boolean
  ) => {
    if (!session?.id) return
    if (amount <= 0) {
      toast.error('Monto inválido')
      return
    }
    setWithdrawing(true)
    try {
      await withdrawCash({ session: session.id, amount, reason, kind, reference })
      if (openDrawer) {
        api.post('/print-jobs/enqueue', { type: 'drawer' }).catch(() => {})
      }
      await fetchData()
      toast.success('Retiro registrado')
      setShowWithdraw(false)
    } catch (err: any) {
      if (err.response?.data?.message) {
        toast.error(err.response.data.message)
      } else {
        toast.error('No se pudo registrar el retiro')
      }
    } finally {
      setWithdrawing(false)
    }
  }

  const handleRefresh = () => {
    if (!session?.id) return
    setRefreshing(true)
    fetchData()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        Cargando...
      </div>
    )
  }

  if (!session || !session.id) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow p-6 space-y-4 w-full max-w-sm"
        >
          <h2 className="text-xl font-semibold text-center">Abrir Caja</h2>
          <p className="text-sm text-center text-gray-600">
            Caja cerrada / abre una sesión para ver totales
          </p>
          {error && <p className="text-red-600 text-sm text-center">{error}</p>}
          <div>
            <label className="block text-sm font-medium">Monto inicial</label>
            <input
              type="number"
              min={1}
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {formError && (
              <p className="text-red-600 text-sm mt-1">{formError}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={opening}
            className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded py-2 disabled:opacity-50 animate-pop"
          >
            {opening ? 'Abriendo...' : 'Abrir sesión'}
          </button>

          <div className="text-center text-xs text-gray-400 mt-2 border-t border-divider pt-3 flex items-center justify-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Hora actual: <span className="font-semibold text-text tabular-nums">{currentTime.toLocaleTimeString('es-CO')}</span>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      {/* Banner de estado de perfil y modo de arqueo */}
      {!isAdmin ? (
        <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-amber-900 dark:text-amber-200 text-xs sm:text-sm shadow-sm">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" />
            <div>
              <span className="font-bold">Modo Arqueo Ciego Activo:</span>
              <span className="ml-1 text-amber-800/90 dark:text-amber-300/80">
                Los montos esperados en caja y totales de ventas están reservados para la Administración para garantizar un arqueo físico transparente.
              </span>
            </div>
          </div>
          <span className="inline-flex items-center self-start sm:self-auto font-semibold px-2.5 py-1 rounded-full bg-amber-200/80 dark:bg-amber-900 text-amber-950 dark:text-amber-100 text-xs">
            Personal / Cajero
          </span>
        </div>
      ) : (
        <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-emerald-900 dark:text-emerald-200 text-xs sm:text-sm shadow-sm">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
            <div>
              <span className="font-bold">Vista de Administrador:</span>
              <span className="ml-1 text-emerald-800/90 dark:text-emerald-300/80">
                Visualización completa de balances en vivo, ventas por medio de pago y cuadre teórico habilitada.
              </span>
            </div>
          </div>
          <span className="inline-flex items-center self-start sm:self-auto font-semibold px-2.5 py-1 rounded-full bg-emerald-200/80 dark:bg-emerald-900 text-emerald-950 dark:text-emerald-100 text-xs">
            Administrador
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4">
        <MetricCard
          icon={<DollarSign />}
          label="Apertura"
          value={formatCOP(summary?.initialAmount ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-blue-500 to-blue-700"
        />
        <MetricCard
          icon={<BarChart2 />}
          label="Ventas Total"
          value={isAdmin ? formatCOP(summary?.totalSales ?? 0) : '🔒 Oculto'}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-green-500 to-green-700"
          subtitle={isAdmin ? 'incluye todos los medios' : 'Arqueo ciego'}
        />
        <MetricCard
          icon={<DollarSign />}
          label="Ventas Efectivo"
          value={isAdmin ? formatCOP(summary?.cashSales ?? 0) : '🔒 Oculto'}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-yellow-400 to-yellow-600"
          subtitle={isAdmin ? 'solo efectivo' : 'Arqueo ciego'}
        />
        <MetricCard
          icon={<CreditCard />}
          label="Ventas Transf."
          value={isAdmin ? formatCOP((summary?.totalSales ?? 0) - (summary?.cashSales ?? 0)) : '🔒 Oculto'}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-cyan-500 to-cyan-700"
          subtitle={isAdmin ? 'otros medios' : 'Arqueo ciego'}
        />
        <MetricCard
          icon={<ArrowDownCircle />}
          label="Retiros"
          value={formatCOP(summary?.withdrawals ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-orange-500 to-orange-700"
        />
        <MetricCard
          icon={<MinusCircle />}
          label="Gastos"
          value={formatCOP(summary?.expenses ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-red-500 to-red-700"
        />
        <MetricCard
          icon={<Calculator />}
          label="Esperado en Caja"
          value={isAdmin ? formatCOP(summary?.expectedInDrawer ?? 0) : '🔒 Arqueo Ciego'}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-purple-500 to-purple-700"
          subtitle={isAdmin ? 'Apertura + Efectivo − Retiros − Gastos' : 'Reservado para Administrador'}
          alert={isAdmin && (summary?.expectedInDrawer ?? 0) < 0}
        />
      </div>
      <SessionPanel
        sessionId={session.id}
        openedAt={formatDate(session.openedAt)}
        onWithdraw={() => setShowWithdraw(true)}
        onClose={() => setShowClose(true)}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      <WithdrawModal
        visible={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        onConfirm={handleWithdraw}
        loading={withdrawing}
      />
      <CloseModal
        visible={showClose}
        onClose={() => setShowClose(false)}
        onConfirm={handleCloseSession}
        loading={closing}
        expectedCash={summary?.expectedInDrawer ?? 0}
        paymentBreakdown={(summary as any)?.paymentBreakdown ?? []}
        isAdmin={isAdmin}
      />
      <MovementList movements={movements} />
    </div>
  )
}
